import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, CheckCircle, CaretDown, CaretUp, Scales, FileText, Lock,
  ArrowRight, CircleNotch, SignOut, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { repApi, clearRepSession, getRepSession } from "@/lib/repApi";

const DOC_ICON = {
  contrato:          FileText,
  politica_comercial: Scales,
  privacidade:       Lock,
  termo_aceite:      ShieldCheck,
};

// Render markdown → minimal HTML. We don't need full MD support — the
// legal docs use a constrained subset (H1/H2/H3, lists, bold, italic,
// horizontal rules, paragraphs). Keep it tight and safe.
function mdToHtml(md) {
  const esc = (s) => s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const lines = md.split("\n");
  let html = "";
  let inUl = false;
  const closeUl = () => { if (inUl) { html += "</ul>"; inUl = false; } };
  for (let raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line.trim()) { closeUl(); html += ""; continue; }
    if (/^---+$/.test(line)) {
      closeUl();
      html += '<hr class="my-5 border-white/10" />'; continue;
    }
    const h3 = line.match(/^###\s+(.*)/);
    if (h3) { closeUl(); html += `<h3 class="font-heading text-[14px] font-semibold tracking-tight text-white mt-5 mb-2">${esc(h3[1])}</h3>`; continue; }
    const h2 = line.match(/^##\s+(.*)/);
    if (h2) { closeUl(); html += `<h2 class="font-heading text-base font-semibold tracking-tight text-white mt-6 mb-2">${esc(h2[1])}</h2>`; continue; }
    const h1 = line.match(/^#\s+(.*)/);
    if (h1) { closeUl(); html += `<h1 class="font-heading text-xl font-semibold tracking-tight text-white mt-2 mb-3">${esc(h1[1])}</h1>`; continue; }
    const li = line.match(/^[-*]\s+(.*)/);
    if (li) {
      if (!inUl) { html += '<ul class="list-disc pl-5 space-y-1 my-2 text-slate-300 text-[13px] leading-relaxed">'; inUl = true; }
      html += `<li>${inline(li[1])}</li>`; continue;
    }
    closeUl();
    html += `<p class="text-slate-300 text-[13px] leading-relaxed my-2">${inline(line)}</p>`;
  }
  closeUl();
  return html;
}

function inline(s) {
  let out = s
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
  out = out.replace(/\*(.+?)\*/g, '<em class="text-slate-200">$1</em>');
  // ☐ checkbox glyph styling
  out = out.replace(/☐/g, '<span class="inline-block w-4 h-4 border border-white/30 rounded-sm align-middle mr-1.5"></span>');
  return out;
}


function DocCard({ doc, kind, scrolledRef }) {
  const [expanded, setExpanded] = useState(false);
  const scrollerRef = useRef(null);
  const Icon = DOC_ICON[kind] || FileText;
  const html = useMemo(() => mdToHtml(doc.content), [doc.content]);

  // Track when the rep has reached the bottom of this doc — used as a
  // UX nudge (we don't BLOCK accept on scroll, but we do mark which
  // docs were genuinely read).
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      scrolledRef.current[kind] = true;
    }
  };

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden"
      data-testid={`legal-doc-${kind}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.04] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <Icon size={18} weight="regular" className="text-slate-400 shrink-0" />
          <div>
            <div className="font-heading text-[14px] font-semibold tracking-tight text-white leading-tight">
              {doc.title}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Versão {doc.version} · publicado em {new Date(doc.published_at).toLocaleDateString("pt-BR")}
            </div>
          </div>
        </div>
        {expanded ? <CaretUp size={14} weight="regular" /> : <CaretDown size={14} weight="regular" />}
      </button>
      {expanded && (
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="max-h-[440px] overflow-y-auto px-5 pb-5 border-t border-white/5"
          data-testid={`legal-doc-${kind}-scroller`}
        >
          <div dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      )}
    </div>
  );
}

export default function RepAcceptTerms() {
  const navigate = useNavigate();
  const session = getRepSession();
  const [bundle, setBundle] = useState(null);
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const scrolledRef = useRef({});

  useEffect(() => {
    if (!session) {
      navigate("/representante/login", { replace: true });
      return;
    }
    repApi.get("/rep/legal/documents").then(({ data }) => setBundle(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!bundle) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center text-slate-500 text-sm">
        <CircleNotch size={20} weight="regular" className="animate-spin" />
      </div>
    );
  }

  const docOrder = ["contrato", "politica_comercial", "privacidade", "termo_aceite"];
  const allChecked = bundle.required_checkboxes.every((c) => checked[c.key]);

  const toggle = (key) => setChecked((prev) => ({ ...prev, [key]: !prev[key] }));

  const onAccept = async () => {
    if (!allChecked) {
      toast.error("Marque todas as declarações obrigatórias para prosseguir.");
      return;
    }
    setSubmitting(true);
    try {
      const accepted_documents = {};
      for (const key of Object.keys(bundle.documents)) {
        accepted_documents[key] = bundle.documents[key].version;
      }
      const payload = {
        documents_version: bundle.bundle_version,
        accepted_documents,
        checkboxes: { ...checked },
      };
      await repApi.post("/rep/legal/accept", payload);
      toast.success("Aceite registrado. Bem-vindo(a) ao Portal!");
      // Refresh session metadata so RepShell knows terms are OK now.
      const repUser = session?.rep || {};
      const updated = { ...repUser, terms_accepted: true };
      localStorage.setItem("agentus_rep_user", JSON.stringify(updated));
      navigate("/representante", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao registrar o aceite. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDecline = () => {
    if (!window.confirm("Tem certeza? Sem o aceite dos termos, seu Portal não é ativado e você será deslogado.")) return;
    clearRepSession();
    navigate("/representante/login", { replace: true });
  };

  const repName = session?.rep?.name || "representante";

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-slate-100 font-body" data-testid="rep-accept-terms-page">
      <header className="border-b border-white/5 bg-[#0A0A0B]/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-[900px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center">
              <ShieldCheck size={18} weight="regular" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">AgentUS</div>
              <div className="font-heading text-[13px] font-semibold tracking-tight leading-none mt-1">
                Aceite do programa
              </div>
            </div>
          </div>
          <button
            onClick={onDecline}
            data-testid="rep-decline-terms"
            className="text-[12px] text-slate-400 hover:text-white inline-flex items-center gap-1.5 transition-colors"
          >
            <SignOut size={12} weight="regular" /> Recusar e sair
          </button>
        </div>
      </header>

      <main className="max-w-[900px] mx-auto px-6 py-8">
        <section className="mb-8">
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">
            Programa de Representantes Autorizados AgentUS
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1 leading-tight">
            Olá, {repName.split(" ")[0]}. Antes de começar, leia e aceite os termos.
          </h1>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">
            Para ativar seu Portal e seu link de indicação, é necessário ler e
            concordar com o conjunto de documentos abaixo. O aceite é
            registrado eletronicamente com IP, data, hora, navegador e versão
            dos documentos — equivalente à assinatura manuscrita
            (MP 2.200-2/2001 e Lei nº 14.063/2020).
          </p>
        </section>

        <section className="space-y-3 mb-8">
          {docOrder.map((kind) => bundle.documents[kind] && (
            <DocCard
              key={kind}
              kind={kind}
              doc={bundle.documents[kind]}
              scrolledRef={scrolledRef}
            />
          ))}
        </section>

        <section
          className="bg-white/[0.04] border border-white/10 rounded-lg p-6 mb-6"
          data-testid="rep-accept-checkboxes"
        >
          <div className="flex items-start gap-2.5 mb-4">
            <Warning size={16} weight="regular" className="text-amber-300 mt-0.5 shrink-0" />
            <p className="text-[13px] text-slate-300 leading-relaxed">
              Marque <strong className="text-white">todas</strong> as declarações abaixo para
              prosseguir. Cada item carrega obrigações jurídicas — leia com atenção.
            </p>
          </div>
          <div className="space-y-2.5">
            {bundle.required_checkboxes.map((c, idx) => (
              <label
                key={c.key}
                className="flex items-start gap-3 p-3 rounded-md hover:bg-white/[0.03] cursor-pointer transition-colors"
                data-testid={`rep-checkbox-${c.key}`}
              >
                <input
                  type="checkbox"
                  checked={!!checked[c.key]}
                  onChange={() => toggle(c.key)}
                  className="mt-0.5 w-4 h-4 accent-emerald-400 cursor-pointer shrink-0"
                />
                <span className="text-[13px] text-slate-300 leading-relaxed">
                  <span className="text-slate-500 mr-1.5 tabular-nums">{(idx + 1)}.</span>
                  {c.label}
                </span>
              </label>
            ))}
          </div>
        </section>

        <section className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Bundle <code className="text-emerald-300 font-mono">{bundle.bundle_version}</code> ·
            integridade <code className="text-emerald-300 font-mono">{bundle.documents_hash.slice(0, 16)}…</code>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onDecline}
              className="text-[13px] text-slate-400 hover:text-white px-4 py-2.5 transition-colors"
            >
              Recusar e sair
            </button>
            <button
              onClick={onAccept}
              disabled={!allChecked || submitting}
              data-testid="rep-accept-submit"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors ${
                allChecked && !submitting
                  ? "bg-emerald-400 text-slate-900 hover:bg-emerald-300"
                  : "bg-white/10 text-slate-500 cursor-not-allowed"
              }`}
            >
              {submitting ? "Registrando…" : "Aceitar e prosseguir"}
              {!submitting && (allChecked ? <CheckCircle size={14} weight="regular" /> : <ArrowRight size={14} weight="regular" />)}
            </button>
          </div>
        </section>

        <p className="text-[11px] text-slate-600 mt-8 leading-relaxed">
          Ao clicar em <em>&ldquo;Aceitar e prosseguir&rdquo;</em>, você registra eletronicamente seu aceite junto à
          AgentUS, com prova de IP, data, hora, navegador e versão dos documentos. O registro é
          imutável e fica disponível para auditorias jurídicas, fiscais e operações de M&amp;A.
        </p>
      </main>
      <Toaster theme="dark" position="top-right" richColors />
    </div>
  );
}
